import { type ReactNode, useEffect, useState } from "react";

import { trackPageComponentedRendered } from "@lib/infrastructure";
import {
  createHaNViewStore,
  HaNContext,
  HaNSearchContext,
  HaNViewStoreContext,
  type HereAndNowTab,
  useHaNSearchContextValue,
} from "@lib/logic";

import { HaNErrorBoundary } from "../shared/HaNErrorBoundary";
import { haNSession } from "../state/session/haNSession";
import { HERE_AND_NOW_TAB_PAGE_IDS } from "../tabs";
import { HaNPageGrid } from "./components/HaNPageGrid";
import { useHaNContextValue } from "./useHaNContextValue";

type HaNPageProps = {
  tab: HereAndNowTab;
  children?: ReactNode;
};

// On phones the app header is hidden, so the page also takes its row and shows the building there
export const HaNPage = ({ tab, children }: HaNPageProps) => {
  return (
    <div className="h-[calc(var(--100vh,100vh)-60px)] [grid-area:fullbody/fullbleed] max-sm:h-[calc(var(--100vh,100vh)-64px)] max-sm:[grid-area:header-start/fullbleed-start/body-end/fullbleed-end]">
      <HaNErrorBoundary
        area="Page"
        resetKeys={[tab]}>
        <HaNPageContent tab={tab}>{children}</HaNPageContent>
      </HaNErrorBoundary>
    </div>
  );
};

const HaNPageContent = ({ tab, children }: HaNPageProps) => {
  const search = useHaNSearchContextValue();
  const value = useHaNContextValue(tab, { isSearching: search.isSearching });
  const [viewStore] = useState(createHaNViewStore);

  // Every way onto a tab counts (tab bar, deep links, back button), so this follows the route instead of clicks
  useEffect(
    function rememberTab() {
      haNSession.rememberTab(tab);
    },
    [tab],
  );

  // Page views count from when the tab shows its first data, so a page left while loading isn't counted
  useEffect(
    function trackTabRenderedWhenLoaded() {
      trackPageComponentedRendered(HERE_AND_NOW_TAB_PAGE_IDS[tab], !value.isTabLoading);
    },
    [value.isTabLoading, tab],
  );

  return (
    <HaNContext.Provider value={value}>
      <HaNViewStoreContext.Provider value={viewStore}>
        <HaNSearchContext.Provider value={search}>
          <HaNPageGrid>{children}</HaNPageGrid>
        </HaNSearchContext.Provider>
      </HaNViewStoreContext.Provider>
    </HaNContext.Provider>
  );
};
